<script lang="ts" setup>
/*
* система уведомлений для отображения toast-сообщений
*
* Основные функции:
* - Отображение временных уведомлений с различными типами (success, error, info)
* - Автоматическое скрытие сообщений по истечении заданного времени
* - Ручное закрытие по крестику в правом верхнем углу
* - Интеграция с системой управления состоянием через feedbackStore
* - Поддержка кастомных иконок для разных типов уведомлений
*
* Особенности:
* - Реактивная синхронизация с глобальным состоянием уведомлений
* - Настраиваемая длительность отображения для разных типов сообщений
* - Визуальное различие типов уведомлений через цветовые схемы и иконки
* - Автоматическое управление временем жизни уведомлений
*/

// Импорты Vue и Pinia
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, ref, watch } from 'vue'

// Импорты хранилищ
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов
import type { IAlertType } from '@/modules/feedback/types/IAlertType'

// Импорты изображений иконок
import toast_error from '@images/icons/toast_error.svg'
import toast_success from '@images/icons/toast_success.svg'

// Инициализация хранилищ
const feedbackStore = useFeedbackStore()

// Деструктуризация реактивных значений из хранилища
const { toastTrigger } = storeToRefs(feedbackStore)

// Реактивные состояния компонента
// Флаг видимости уведомления
const showAlert = ref(false)

// Текст сообщения уведомления
const alertMessage = ref('')

// Заголовок уведомления
const alertTitle = ref('')

// Тип/серьезность уведомления (success, error, info)
const alertSeverity = ref<IAlertType>('info')

// Время отображения уведомления в миллисекундах
const alertTimeout = ref(3000)

let hideTimer: number | undefined

const clearHideTimer = () => {
  if (hideTimer !== undefined) {
    window.clearTimeout(hideTimer)
    hideTimer = undefined
  }
}

const closeAlert = () => {
  clearHideTimer()
  showAlert.value = false
}

/**
 * Вычисляемое свойство для определения иконки уведомления
 *
 * Назначение:
 * - Динамически определяет SVG-иконку на основе типа уведомления
 * - Обеспечивает визуальное различие между типами сообщений
 * - Возвращает undefined для типов, не требующих кастомных иконок
 *
 * Поддерживаемые типы:
 * - success: возвращает toast_success (зеленая иконка успеха)
 * - error: возвращает toast_error (красная иконка ошибки)
 * - info/warning: возвращает undefined (используются стандартные иконки Vuetify)
 *
 * Возвращает:
 * - string | undefined: путь к SVG-иконке или undefined
 */
const icon = computed(() => {
  if (alertSeverity.value === 'success') return toast_success
  if (alertSeverity.value === 'error') return toast_error
  return undefined
})

/**
 * Вотчер для отслеживания изменения триггера уведомлений
 *
 * Назначение:
 * - Реагирует на изменения глобального состояния уведомлений
 * - Синхронизирует локальные состояния с данными из feedbackStore
 * - Автоматически показывает и скрывает уведомления по таймауту
 *
 * Алгоритм:
 * 1. При срабатывании toastTrigger обновляет все локальные состояния
 * 2. Устанавливает showAlert в true для отображения уведомления
 * 3. Запускает таймер для автоматического скрытия уведомления
 * 4. По истечении alertTimeout скрывает уведомление
 */
watch(toastTrigger, () => {
  clearHideTimer()
  alertMessage.value = feedbackStore.toast.detail
  alertSeverity.value = feedbackStore.toast.severity
  alertTimeout.value = feedbackStore.toast.life
  showAlert.value = true

  hideTimer = window.setTimeout(() => {
    showAlert.value = false
    hideTimer = undefined
  }, alertTimeout.value)
})

onUnmounted(() => {
  clearHideTimer()
})
</script>

<template>
  <VAlert
    v-model="showAlert"
    :icon="icon"
    variant="outlined"
    :text="alertMessage"
    :title="alertTitle"
    :type="alertSeverity"
    closable
    close-icon="ri-close-line"
    class="alert-toast"
    @click.stop
    @click:close.stop="closeAlert"
  />
</template>

<style scoped lang="scss">
.alert-toast {
  position: fixed;
  top: 50px;
  right: 20px;
  z-index: 100005;
  max-width: 400px;
  width: fit-content;
  background-color: rgb(var(--v-theme-surface));
}
</style>
